import {useCallback, useEffect, useRef, useState} from 'react';

export function useManualTimeout(timeoutDelayMs: number | null) {
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const run = useCallback(
    function runTimer() {
      setHasTimedOut(false);

      if (timeoutDelayMs === null) {
        return;
      }

      const timeout = setTimeout(() => {
        setHasTimedOut(true);
      }, timeoutDelayMs);

      return () => {
        clearTimeout(timeout);
        setHasTimedOut(false);
      };
    },
    [timeoutDelayMs],
  );

  return [hasTimedOut, run] as const;
}

export const useTimedOut = (timeoutDelayMs: number | null) => {
  const [hasTimedOut, run] = useManualTimeout(timeoutDelayMs);

  useEffect(() => {
    const clear = run();
    return clear;
  }, [run]);

  return hasTimedOut;
};

export const usePausableTimeout = (
  timeoutDelayMs: number | null,
  options?: {isPaused?: boolean},
) => {
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);
  const remainingTime = useRef<number>(timeoutDelayMs ?? 0);
  const startTime = useRef<number>(0);

  useEffect(() => {
    if (options?.isPaused) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        const elapsed = Date.now() - startTime.current;
        remainingTime.current = Math.max(0, remainingTime.current - elapsed);
      }
      return;
    }

    if (timeoutDelayMs === null || hasTimedOut) {
      return;
    }

    startTime.current = Date.now();
    timeoutId.current = setTimeout(() => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setHasTimedOut(true);
    }, remainingTime.current);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [timeoutDelayMs, options?.isPaused, hasTimedOut]);

  return hasTimedOut;
};
